import { useEffect, useRef } from 'react';

// Radios are in here because their group moves the choice with the arrows, as a native
// radio did when the detection filters were `<input type="radio">`. Sliders, the filmstrip's
// day scrubber among them, step their own value with the arrows.
const EDITABLE_SELECTOR =
    'input, textarea, select, [role="radio"], [role="slider"], [contenteditable=""], [contenteditable="true"]';

interface UseViewerKeyboardArgs {
    /** Left/right arrows step through the images. */
    onStep: (direction: 'next' | 'previous') => void;
    /** Holding SHIFT turns the magnifier on, releasing it turns it off. */
    onMagnifierHeld?: (held: boolean) => void;
    arrowNavigationEnabled?: boolean;
    magnifierShortcutEnabled?: boolean;
}

/**
 * The viewer's two keyboard shortcuts, in one listener.
 *
 * Bound on `document` rather than the viewer so both work without clicking the image
 * first. They used to be two separate effects with two separate listeners, one capturing
 * and one not, plus a manual "latest ref" for the navigation callback.
 *
 * Capture phase matters: a focusable control that calls preventDefault() on the arrow keys
 * would otherwise own them, and a bubble-phase listener only ever saw an already
 * defaultPrevented event, so the images never moved. Capturing claims the key first, and
 * stopPropagation keeps that control from also acting on it. Controls whose arrows are their
 * own, such as the day scrubber (`role="slider"`), are left alone by EDITABLE_SELECTOR.
 */
export function useViewerKeyboard({
    onStep,
    onMagnifierHeld,
    arrowNavigationEnabled = true,
    magnifierShortcutEnabled = true,
}: UseViewerKeyboardArgs): void {
    /* The listeners are bound once, but they have to call the *current* callbacks: binding
       them into the listener would capture the image index from the render that bound it and
       never advance past the second image. */
    const handlers = useRef({ onStep, onMagnifierHeld });
    handlers.current = { onStep, onMagnifierHeld };

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (magnifierShortcutEnabled && event.shiftKey) {
                handlers.current.onMagnifierHeld?.(true);
            }

            if (!arrowNavigationEnabled) {
                return;
            }
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                return;
            }

            // Never hijack a shortcut: SHIFT drives the magnifier, and browsers use
            // ALT/CMD + arrow for history navigation.
            if (
                event.defaultPrevented ||
                event.shiftKey ||
                event.altKey ||
                event.ctrlKey ||
                event.metaKey
            ) {
                return;
            }

            /* Typing in a field, or moving the caret in one, must win over navigation -
               this page has date pickers and filter inputs on it. `event.target` is only an
               Element for focused nodes; it is `document` when nothing has focus, and
               `document` has no `closest`. */
            if (event.target instanceof Element && event.target.closest(EDITABLE_SELECTOR)) {
                return;
            }

            event.preventDefault(); // stop the page/filmstrip scrolling sideways as well
            event.stopPropagation(); // keep a focused control from also acting on the key
            handlers.current.onStep(event.key === 'ArrowRight' ? 'next' : 'previous');
        };

        const onKeyUp = (event: KeyboardEvent) => {
            if (magnifierShortcutEnabled && event.key === 'Shift') {
                handlers.current.onMagnifierHeld?.(false);
            }
        };

        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('keyup', onKeyUp, true);

        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('keyup', onKeyUp, true);
        };
    }, [arrowNavigationEnabled, magnifierShortcutEnabled]);
}
